
import { ReasonCode } from "@enums/ReasonCode";
import { IPlot, IPlotEvent } from "@interfaces/IPlot";
import { IVehicle } from "@interfaces/IVehicle";
import { IVehicleInput } from "@interfaces/IVehicleInput";
import { ChartService } from "@services/charts.service";
import { GoogleService } from "@services/google.service";
import { PlotEventService } from "@services/plotEvent.service";
import { TranslationService } from "@services/translation.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import { VehicleInputService } from "@services/vehicleInput.service";
import { VeriDate } from "@services/veriDate.service";
import { VeriMarkerService } from "@services/veriMarker.service";
import * as angular from "angular";
import * as Highcharts from "highcharts";
import * as moment from "moment";
import "./journeyGraph.scss";

var KalmanFilter = require('kalmanjs');

export interface IJourneyGraphExtremes {
    min: number;
    max: number;
}

interface IJourneyGraphComponentBindings {
    highlightedPlot: IPlot;
    plots: IPlot[];
    events: IPlotEvent[];

    extremes: IJourneyGraphExtremes;
    onExtremesUpdated: (event: { $extremes: IJourneyGraphExtremes }) => void;

    onPlotPointHover: (event: { $plotPoint: IPlotEvent, $mouseXPos: number }) => void;

    start: Date;
    end: Date;
    selectedVehicle: IVehicle;

    loading: boolean;
}

interface ISpeedLimitResponse {
    speedData: [number, number][];
    zones?: Highcharts.AreaZone[];
}

class JourneyGraphComponentController implements IJourneyGraphComponentBindings, angular.IComponentController {

    noDataHighChartsTitle = { /*text: "No Data Available",*/ align: "center", verticalAlign: "middle" };
    highlightedPlot: IPlot;
    plots: IPlot[];
    events: IPlotEvent[];
    loading = false;

    start: Date;
    end: Date;
    selectedVehicle: IVehicle;

    extremes: IJourneyGraphExtremes;
    onExtremesUpdated: (event: { $extremes: IJourneyGraphExtremes }) => void;

    onPlotPointHover: (event: { $plotPoint: IPlot, $mouseXPos: number }) => void;

    lastGraphHighlightX = 0;

    showChartType = 0;

    displayChartConfig: Highcharts.Options;

    highlightedPlotPosition: google.maps.Point;

    hideTemperature = true;

    vehicleInputs: IVehicleInput[];

    static $inject = ["$scope", "googleService", "unitConversionService", "vehicleInputService", "plotEventService", "chartService", "translationService"];

    constructor(
        private readonly $scope: ng.IScope,
        private readonly googleService: GoogleService,
        private readonly unitConversionService: UnitConversionService,
        private readonly vehicleInputService: VehicleInputService,
        private readonly plotEventService: PlotEventService,
        private readonly chartService: ChartService,
        private readonly translationService: TranslationService
    ) {
    }

    $onInit() {
        return this.vehicleInputService
            .getVehicleInputs()
            .then(inputs => {
                this.vehicleInputs = inputs;
            });
    }

    $onChanges(onChangesObj: angular.IOnChangesObject) {

        // See if our extremes object has been changed via external update
        if (onChangesObj["extremes"]) {
            this.setExtremes();
        }

        if (onChangesObj["plots"]/* && !onChangesObj["plots"].isFirstChange()*/) {
            // Filter out start and end wakeup plots to help the graph draw better
            var firstNonWakeupPlot = this.plots.findIndex(p => p.Reason != ReasonCode.Wakeup);
            this.plots = this.plots.slice(firstNonWakeupPlot);

            var lastNonWakeupPlot = this.plots.reverse().findIndex(p => p.Reason != ReasonCode.Wakeup);
            this.plots = this.plots.slice(lastNonWakeupPlot);
            this.plots.reverse();

            this.redrawCharts();
        }
    }

    private getHighchartsObject(): Highcharts.ChartObject {
        return (this.displayChartConfig as any).getChartObj() as Highcharts.ChartObject;
    }

    highlightPlotPointOnGraph(plot: IPlot) {

        const chart = this.getHighchartsObject();

        // Find highcharts point
        for (let i = 0; i < chart.series[0].data.length; i++) {
            if (chart.series[0].data[i].x === plot.UtcMoment.valueOf()) {
                for (let j = 0; j < chart.series.length; j++) {
                    (chart.series[i].data[j] as any).setState("hover");
                }

                (chart as any).tooltip.refresh([chart.series[0].data[0], chart.series[1].data[0]]);
            }
            else {
                for (let j = 0; j < chart.series.length; j++) {
                    (chart.series[i].data[j] as any).setState();
                }
            }
        }
    }

    private findPointBasedOnX(x: number) {
        for (let i = 0; i < this.plots.length; i++) {
            const plot = this.plots[i];
            if (plot.UtcMoment.valueOf() === x) {
                return plot;
            }
        }

        return null;
    }

    private highlightPlotBasedOnX(x: number, clientX: number) {
        if (this.lastGraphHighlightX === x) {
            return;
        }

        const plot = this.findPointBasedOnX(x);
        if (!plot) {
            return;
        }

        this.lastGraphHighlightX = x;

        this.highlightedPlot = plot;

        this.$scope.$applyAsync(() => {
            this.onPlotPointHover({ $plotPoint: plot, $mouseXPos: clientX });
        });
    }

    updateJourneyPlot(plot: IPlot, clientX: number) {
        this.onPlotPointHover({ $plotPoint: plot, $mouseXPos: clientX });
    }

    redrawCharts() {
        this.events = [];
        this.highlightedPlot = null;
        this.displayChartConfig = null;

        switch (this.showChartType) {
            case 0:
                this.displayChartConfig = this.createSpeedChartConfig();
                break;

            case 1:
                this.displayChartConfig = this.createTorqueChartConfig();
                break;

            case 2:
                this.displayChartConfig = this.createFuelChartConfig();
                break;
        }

        if (this.displayChartConfig) {
            this.chartService.setOnLoadCallback(this.displayChartConfig, chartObject => { this.setExtremes(chartObject); })
        }
    }

    private getElevationSeriesData(): angular.IPromise<[number, number][]> {
        //this.chartConfig.series[2].data = [];

        var canPlots = this.plots.filter(p => p.Reason === ReasonCode.FmsOn || this.isTimedCANPlot(p));

        return this.googleService
            .updatePlotsElevation(canPlots)
            .then(() => {
                var seriesData: [number, number][] = [];

                for (let i = 0; i < canPlots.length; i++) {
                    const updatedPlot = canPlots[i];

                    seriesData.push([updatedPlot.UtcMoment.valueOf(), this.chartService.roundValue(updatedPlot.elevation)]);
                }

                return seriesData;
            })
            .catch(() => {
                console.error("updateElevationForPlots.fail");
                return [];
            });
    }

    private setExtremes(chartObject?: Highcharts.ChartObject) {
        if (!this.extremes || !this.displayChartConfig) {
            return;
        }

        if (!chartObject) {
            chartObject = this.getHighchartsObject();
        }

        if (!chartObject || !chartObject.xAxis) {
            return;
        }

        if (chartObject.xAxis.length) {
            chartObject.xAxis.forEach(axis => {
                axis.setExtremes(this.extremes.min, this.extremes.max);
            });
        }
    }

    private getSpeedLimitsSeriesData(): ISpeedLimitResponse {
        var filteredPlots = this.plots;

        var returnValue: ISpeedLimitResponse = { speedData: [], zones: [] };

        var colorArray = this.getStandardChartOptions().colors;

        var previousPoint: IPlot;

        var speeding = false;
        for (let i = 0; i < filteredPlots.length; i++) {
            const updatedPlot = filteredPlots[i];

            returnValue.speedData.push([updatedPlot.UtcMoment.valueOf(), this.unitConversionService.convertToUserUnit(updatedPlot.SpeedLimit, UnitConversionType.Speed)]);

            // Start from midpoint
            let xValue = updatedPlot.UtcMoment.valueOf();
            if (previousPoint) {
                xValue += previousPoint.UtcMoment.valueOf();
                xValue /= 2;
            }

            if (!speeding && updatedPlot.Velocity > updatedPlot.SpeedLimit) {

                // End of Blue zone
                returnValue.zones.push({
                    color: colorArray[0],
                    value: xValue
                });

                speeding = true;
            }
            else if (speeding && updatedPlot.Velocity <= updatedPlot.SpeedLimit) {

                // End of Speeding zone
                returnValue.zones.push({
                    color: colorArray[1],
                    fillColor: this.chartService.getGradientFade(colorArray[1].toString(), 0.5) as any,
                    value: xValue
                });

                speeding = false;
            }

            previousPoint = updatedPlot;
        }

        return returnValue;
    }

    private getTorqueDataPoint(plot: IPlot, previousPlot: IPlot): [number, number] {
        if (!plot.SensorData) {
            console.warn("No Can Params for CAN Plot");
            return;
        }

        if (!previousPlot || !previousPlot.SensorData) {
            return;
        }

        const diffSeconds = plot.UtcMoment.diff(previousPlot.UtcMoment, "s");

        const torqueOver = plot.SensorData.OverTorqueDuration - previousPlot.SensorData.OverTorqueDuration;
        const diffPercentage = torqueOver * 100 / diffSeconds;
        const torqueDataPoint: [number, number] = [previousPlot.UtcMoment.valueOf(), this.chartService.roundValue(diffPercentage, 2)];

        return torqueDataPoint;
    }

    private getRPMDataPoint(plot: IPlot, previousPlot: IPlot): [number, number] {
        if (!plot.SensorData) {
            console.warn("No Can Params for CAN Plot");
            return;
        }

        if (!previousPlot || !previousPlot.SensorData) {
            return;
        }

        const diffSeconds = plot.UtcMoment.diff(previousPlot.UtcMoment, "s");
        const rpmOver = plot.SensorData.OverRevDuration - previousPlot.SensorData.OverRevDuration;
        const diffPercentage = rpmOver * 100 / diffSeconds;
        const rpmDataPoint: [number, number] = [previousPlot.UtcMoment.valueOf(), this.chartService.roundValue(diffPercentage, 2)];

        return rpmDataPoint;
    }

    private getAxleWeightSeriesData(): [number, number][] {
        var seriesData: [number, number][] = [];

        for (let i = 0; i < this.plots.length; i++) {
            var plot = this.plots[i];

            if (plot.SensorData?.TotalWeight) {
                seriesData.push([plot.UtcMoment.valueOf(), plot.SensorData.TotalWeight]);
            }
        }

        return seriesData;
    }

    private getSpeedSeriesData(): [number, number][] {
        let telematicsOnPlot: IPlot[] = [];

        var speedData: [number, number][] = [];

        const hasCANPTOEvents = this.plotEventService.hasCANPTOEvents(this.plots);

        for (let i = 0; i < this.plots.length; i++) {
            var plot = this.plots[i];

            if (plot.Reason !== ReasonCode.Fms) {
                speedData.push([
                    plot.UtcMoment.valueOf(), this.unitConversionService.convertToUserUnit(plot.Velocity, UnitConversionType.Speed)
                ]);
            }
            //else if (this.isTimedCANPlot(plot)) {

            //    const totalAxleWeight = plot.CanParams.AxleWeight0 +
            //        plot.CanParams.AxleWeight1 +
            //        plot.CanParams.AxleWeight2 +
            //        plot.CanParams.AxleWeight3;

            //    this.axleWeightData.push([plot.UtcMoment.valueOf(), totalAxleWeight]);
            //}
            //else if (plot.CanParams && plot.CanParams.Header === "$EVT") {

            //    if (plot.CanParams.EventCode === PlotExtensionEventType.PTO) { // PTO ON

            //        if (plot.CanParams.EventTime) {

            //            var band = this.addXAxisPlotBand(plot.UtcMoment,
            //                plot.CanParams.EventTime,
            //                "rgba(228,139,84,0.4)",
            //                "rgb(228,139,84)",
            //                "PTO");

            //            band.label.y = 50; // allows for overlap with idle
            //        }
            //    }
            //    else if (plot.CanParams.EventCode === PlotExtensionEventType.Idling) { // Idling 
            //        if (plot.CanParams.EventTime) {

            //            this.addXAxisPlotBand(plot.UtcMoment,
            //                plot.CanParams.EventTime,
            //                "rgba(228,211,84,0.4)",
            //                "rgb(228,211,84)",
            //                "Idling");
            //        }
            //    }
            //}

            //if (!hasCANPTOEvents && plot.Reason === ReasonCode.Telematics && plot.Telematics) {
            //    const input = this.vehicleInputService.getInputForVehicle(plot.VehicleId, this.vehicleInputs);
            //    if (input) {

            //        plot.Telematics.forEach(telematics => {
            //            if (telematics.State === input.ActiveState) {
            //                telematicsOnPlot[telematics.BitPos] = plot;
            //            }
            //            else if (telematicsOnPlot[telematics.BitPos]) {
            //                const duration = plot.UtcMoment.diff(telematicsOnPlot[telematics.BitPos].UtcMoment, "s")
            //                this.addXAxisPlotBand(plot.UtcMoment,
            //                    duration,
            //                    "rgba(228,211,84,0.4)",
            //                    "rgb(228,211,84)",
            //                    input.Label);
            //            }
            //        });
            //    }
            //}
        }

        return speedData;
    }

    private getTorqueDataSeries(): [number, number][] {
        var seriesData: [number, number][] = [];

        let previousTimedCANPlot: IPlot = null;

        this.plots.forEach(plot => {
            if (plot.Reason === ReasonCode.FmsOn || this.isTimedCANPlot(plot)) {

                var point = this.getTorqueDataPoint(plot, previousTimedCANPlot);
                if (point) {
                    seriesData.push(point);
                }

                previousTimedCANPlot = plot;
            }
        });

        return seriesData;
    }

    private getRPMDataSeries(): [number, number][] {
        var seriesData: [number, number][] = [];

        let previousTimedCANPlot: IPlot = null;

        this.plots.forEach(plot => {
            if (plot.Reason === ReasonCode.FmsOn || this.isTimedCANPlot(plot)) {

                var point = this.getRPMDataPoint(plot, previousTimedCANPlot);
                if (point) {
                    seriesData.push(point);
                }

                previousTimedCANPlot = plot;
            }
        });

        return seriesData;
    }

    private getFuelDataSeries(): [number, number][] {

        var seriesData: [number, number][] = [];
        var differenceTotal = 0;

        var fuelKalmanFilter = new KalmanFilter({ R: 5, Q: 2 });

        this.plots.forEach(plot => {
            if (plot.SensorData && plot.SensorData.FuelLevel) {
                const fuelLevel = plot.SensorData.FuelLevel;
                const filteredLevel = fuelKalmanFilter.filter(fuelLevel);

                differenceTotal += Math.abs(fuelLevel - filteredLevel);

                const fuelDataPoint: [number, number] = [plot.UtcMoment.valueOf(), Math.round(filteredLevel)];
                seriesData.push(fuelDataPoint);
            }
        });

        return seriesData;
    }

    private isTimedCANPlot(plot: IPlot) {
        return plot.Reason === ReasonCode.Fms && plot.SensorData && plot.SensorData.Header === "K0 03 041";
    }

    //private addXAxisPlotBand(endTime: moment.Moment, durationSeconds: number, backgroundColor: string, borderColor: string, label: string): Highcharts.PlotBands {

    //    const band: Highcharts.PlotBands = {
    //        color: backgroundColor,
    //        borderWidth: 1,
    //        borderColor: borderColor,
    //        label: {
    //            text: label,
    //            align: "center",
    //            style: {
    //                color: "rgba(0,0,0,0.6)"
    //            }
    //        },
    //        from: endTime.clone().add(-durationSeconds, "seconds").valueOf(),
    //        to: endTime.valueOf() // End of the plot band
    //    };

    //    (this.displayChartConfig.xAxis as Highcharts.AxisOptions).plotBands.push(band);

    //    return band;
    //}

    // Need to make these generic
    private addYAxisPlotBand(from: number, to: number, backgroundColor: string, borderColor: string, label: string): Highcharts.PlotBands {

        var band: Highcharts.PlotBands = {
            color: backgroundColor,
            borderWidth: 1,
            borderColor: borderColor,
            from: from,
            to: to // End of the plot band
        };

        const yAxisPadding = 3;
        const paddedFrom = from - yAxisPadding;
        const paddedTo = to + yAxisPadding;

        const yAxisOptions = (this.displayChartConfig.yAxis[0] as Highcharts.AxisOptions);
        yAxisOptions.plotBands = yAxisOptions.plotBands || [];
        yAxisOptions.plotBands.push(band);

        yAxisOptions.min = isNaN(yAxisOptions.min) ? paddedFrom : Math.min(yAxisOptions.min, paddedFrom);
        yAxisOptions.max = isNaN(yAxisOptions.max) ? paddedTo : Math.max(yAxisOptions.max, paddedTo);

        return band;
    }

    private createSpeedChartConfig(): Highcharts.Options {

        const highchartsOptions = this.getStandardChartOptions();

        highchartsOptions.chart.alignTicks = true;
        highchartsOptions.plotOptions.spline.dashStyle = "longdash";
        //highchartsOptions.tooltip.enabled = false;

        highchartsOptions.yAxis = [
            {
                title: { text: `Speed (${this.unitConversionService.getConversionLabel(UnitConversionType.Speed)})` },
                min: 0,
                tickAmount: 4
            },
            {
                title: { text: "Axle Weight (Kg)" },
                opposite: true
            }
        ];

        highchartsOptions.series = [
            {
                id: "1",
                name: `Vehicle Speed (${this.unitConversionService.getConversionLabel(UnitConversionType.Speed)})`,
                data: this.getSpeedSeriesData(),
                type: "areaspline",
                zIndex: 2,
                yAxis: 0
            }, {
                id: "2",
                name: "Axle Weight",
                data: this.getAxleWeightSeriesData(),
                type: "spline",
                zIndex: 0,
                yAxis: 1
            }
        ];

        const speedLimitResponse = this.getSpeedLimitsSeriesData();
        if (speedLimitResponse) {
            highchartsOptions.series[1].data = speedLimitResponse.speedData;
            highchartsOptions.plotOptions.areaspline.zones = speedLimitResponse.zones;
        }

        this.setExtremes();

        return highchartsOptions;
    }

    private createTorqueChartConfig(): Highcharts.Options {
        const highchartsOptions = this.getStandardChartOptions();
        //this.chartService.usePercentageTooltipFormatter(highchartsOptions);
        //highchartsOptions.tooltip.enabled = false;
        highchartsOptions.chart.alignTicks = false;

        highchartsOptions.yAxis = [
            {
                title: {
                    text: "% Time Over"
                },
                min: 0,
                max: 50
            }, {
                title: {
                    text: "Elevation (m)"
                },
                opposite: true
            }
        ];

        highchartsOptions.series = [
            {
                id: "1",
                name: "% Time Over Torque",
                data: this.getTorqueDataSeries(),
                type: "spline",
                step: true,
                zIndex: 0,
                yAxis: 0
            } as any /* needed for step */, {
                id: "2",
                name: "% Time Over Revving",
                data: this.getRPMDataSeries(),
                type: "spline",
                step: true,
                zIndex: 0,
                yAxis: 0
            } as any /* needed for step */, {
                id: "3",
                name: "Estimated Elevation (m)",
                data: [],
                type: "spline",
                zIndex: 0,
                yAxis: 1
            }
        ];

        this.getElevationSeriesData().then(seriesData => {
            highchartsOptions.series[2].data = seriesData;
        });

        return highchartsOptions;
    }

    private createFuelChartConfig(): Highcharts.Options {
        const highchartsOptions = this.getStandardChartOptions();

        highchartsOptions.chart.alignTicks = false;

        highchartsOptions.yAxis = [
            {
                title: {
                    text: "% " + this.translationService.t("JourneyGraphComponent.FuelLevel")
                },
                min: 0,
                max: 100
            }
        ];

        highchartsOptions.series = [
            {
                id: "1",
                name: "% Fuel Level",
                data: this.getFuelDataSeries(),
                step: true,
                type: "spline",
                zIndex: 0,
                yAxis: 0
            } as any
        ];

        return highchartsOptions;
    }

    private getStandardChartOptions(): Highcharts.Options {
        var controller: JourneyGraphComponentController = this;
        //var mapService = this.mapService;
        var $scope = this.$scope;

        var highchartsOptions = this.chartService.createBaseChartOptions("line");

        highchartsOptions.chart.spacingTop = 10;
        highchartsOptions.chart.spacingLeft = 10;
        highchartsOptions.xAxis = {
            type: 'datetime'
        }
        highchartsOptions.chart.spacingRight = 10;
        highchartsOptions.colors = ["#00426a", "#cf4946", "#555555"];

        this.chartService.setLegendOptions(highchartsOptions, {
            enabled: true,
            align: "right",
            verticalAlign: "top",
            itemMarginTop: -5
        });

        this.chartService.subscribeToMouseEvents(highchartsOptions, "mouseOver", (x, clientX) => {
            this.highlightPlotBasedOnX(x, clientX);
        })

        return highchartsOptions;
    }
}

export class JourneyGraphComponent implements angular.IComponentOptions {
    bindings: any;
    controller: any;
    template: string;

    constructor() {
        this.bindings = {
            highlightedPlot: "<",
            plots: "<",
            loading: "<",
            events: "<",

            selectedVehicle: "<",
            start: "<",
            end: "<",

            extremes: "<",
            onExtremesUpdated: "&",

            onPlotPointHover: "&"
        };

        this.controller = JourneyGraphComponentController;
        this.template = require("./journeyGraph.html");
    }
}
